import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCcw, TrendingUp, Search } from 'lucide-react';
import { Token } from '../../types/supabase';
import { getTokenChartData, OHLCVData } from '../../services/solanatrackerService';
import TokenSearchModal from '../../components/TokenSearchModal';
import customDatafeed from './datafeed';

// Define the widget constructor which is loaded from the window
declare global {
  interface Window {
    TradingView: {
      widget: any;
    }
  }
}

// Interface for the widget options
interface ChartingLibraryWidgetOptions {
  symbol: string;
  interval?: string;
  container: string;
  datafeed: any;
  library_path: string;
  locale?: string;
  disabled_features?: string[];
  enabled_features?: string[];
  theme?: string;
  autosize?: boolean;
  overrides?: any;
  time_frames?: any[];
}

// Interface for the widget instance
interface IChartingLibraryWidget {
  onChartReady(callback: () => void): void;
  remove(): void;
  resize(width: number, height: number): void;
}

interface SimpleChartProps {
  selectedToken?: Token;
}

const SimpleChart: React.FC<SimpleChartProps> = ({ selectedToken }) => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [chartInstance, setChartInstance] = useState<IChartingLibraryWidget | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [chartData, setChartData] = useState<OHLCVData[]>([]);
  const [scriptsLoaded, setScriptsLoaded] = useState<boolean>(false);
  
  // Default to 1h timeframe
  const currentTimeframe = '1h';
  
  // Token search state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [localSelectedToken, setLocalSelectedToken] = useState<Token | undefined>(selectedToken);
  
  // Use the local token state if available, otherwise use the prop
  const effectiveToken = localSelectedToken || selectedToken;
  
  // Convert timeframe to TradingView resolution (for chart display)
  const timeframeToResolution = useCallback((timeframe: string): string => {
    switch (timeframe) {
      case '1h': return '60';
      case '4h': return '240';
      case '1d': return 'D';
      default: return '60'; // Default to 1h
    }
  }, []);
  
  // Update local state when prop changes
  useEffect(() => {
    if (selectedToken) {
      setLocalSelectedToken(selectedToken);
      // Force chart re-initialization by temporarily clearing chart instance
      if (chartInstance) {
        try {
          chartInstance.remove();
          setChartInstance(null);
        } catch (err) {
          console.warn('Error removing chart instance during token change:', err);
        }
      }
    }
  }, [selectedToken]);

  // Load TradingView scripts
  useEffect(() => {
    const loadScripts = async () => {
      try {
        // Check if scripts are already loaded
        if (window.TradingView && window.TradingView.widget) {
          setScriptsLoaded(true);
          return;
        }

        // Load charting library script - use web path
        const chartingLibraryScript = document.createElement('script');
        chartingLibraryScript.src = '/charting_library/charting_library.standalone.js';
        chartingLibraryScript.async = true;
        
        document.head.appendChild(chartingLibraryScript);
        
        // Wait for scripts to load
        chartingLibraryScript.onload = () => {
          setScriptsLoaded(true);
        };

        chartingLibraryScript.onerror = (error) => {
          console.error('Error loading charting library:', error);
        };
      } catch (error) {
        console.error('Error loading TradingView scripts:', error);
      }
    };
    
    loadScripts();
  }, []);

  // Handle window resize for responsive chart
  useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current && chartInstance) {
        const containerElement = chartContainerRef.current;
        const { clientWidth, clientHeight } = containerElement;
        
        // Resize the chart if it exists
        if (typeof chartInstance.resize === 'function') {
          chartInstance.resize(clientWidth, clientHeight);
        }
      }
    };

    // Do initial resize
    updateDimensions();
    
    // Also listen to window resize
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [chartInstance]);
  
  // Fetch initial chart data
  useEffect(() => {
    if (effectiveToken?.mint) {
      fetchChartData(effectiveToken.mint, currentTimeframe);
    }
  }, [effectiveToken?.mint]);

  // Initialize the TradingView chart
  useEffect(() => {
    if (!chartContainerRef.current || !effectiveToken?.mint || !scriptsLoaded) {
      return;
    }
    
    console.log(`Initializing chart for token: ${effectiveToken.mint}`);
    
    // Clean up existing chart instance
    if (chartInstance) {
      console.log('Removing existing chart instance');
      try {
        chartInstance.remove();
      } catch (err) {
        console.warn('Error removing existing chart instance:', err);
      }
      setChartInstance(null);
    }
    
    // Verify TradingView library is loaded
    if (!window.TradingView || !window.TradingView.widget) {
      console.error('TradingView library not loaded, check the script tag in index.html');
      return;
    }
    
    // Clear the container
    if (chartContainerRef.current.children.length > 0) {
      chartContainerRef.current.innerHTML = '';
    }
    
    // Create a unique ID for the chart container
    const containerId = `tv_chart_container_${effectiveToken.mint}_${Date.now()}`;
    const containerElement = document.createElement('div');
    containerElement.id = containerId;
    containerElement.style.width = '100%';
    containerElement.style.height = '100%';
    containerElement.style.position = 'absolute';
    containerElement.style.top = '0';
    containerElement.style.left = '0';
    
    chartContainerRef.current.appendChild(containerElement);
    
    // Initialize widget
    const widgetOptions: ChartingLibraryWidgetOptions = {
      symbol: effectiveToken.mint,
      datafeed: customDatafeed,
      interval: timeframeToResolution(currentTimeframe),
      container: containerId,
      library_path: '/charting_library/',
      locale: 'en',
      disabled_features: [
        'use_localstorage_for_settings',
        'header_symbol_search',
        'header_compare',
        'header_screenshot',
        'border_around_the_chart',
        'volume_force_overlay',
        'timeframes_toolbar', // Hide the timeframes toolbar
        'create_volume_indicator_by_default' // Disable volume indicator by default
      ],
      enabled_features: [
        'save_chart_properties_to_local_storage',
        'remove_library_container_border'
      ],
      autosize: true,
      theme: 'Dark',
      overrides: {
        "mainSeriesProperties.style": 1, // Candles
        "mainSeriesProperties.candleStyle.upColor": "#47be8f",
        "mainSeriesProperties.candleStyle.downColor": "#ef4444",
        "mainSeriesProperties.candleStyle.drawWick": true,
        "mainSeriesProperties.candleStyle.drawBorder": true,
        "mainSeriesProperties.candleStyle.borderUpColor": "#47be8f",
        "mainSeriesProperties.candleStyle.borderDownColor": "#ef4444",
        "mainSeriesProperties.candleStyle.wickUpColor": "#47be8f",
        "mainSeriesProperties.candleStyle.wickDownColor": "#ef4444",
        "paneProperties.background": "#131722",
        "paneProperties.vertGridProperties.color": "rgba(255, 255, 255, 0.06)",
        "paneProperties.horzGridProperties.color": "rgba(255, 255, 255, 0.06)",
        "scalesProperties.textColor": "#999999",
        "volumePaneSize": "tiny" // Set volume pane to smallest size possible in case it's enabled manually
      }
    };
    
    try {
      // Create new widget instance
      const widget = new window.TradingView.widget(widgetOptions);
      
      // On chart ready callback
      widget.onChartReady(() => {
        console.log('TradingView chart is ready');
        setChartInstance(widget);
      });
    } catch (err) {
      console.error('Error initializing TradingView chart:', err);
    }
    
    // Cleanup on unmount
    return () => {
      if (chartInstance) {
        try {
          chartInstance.remove();
        } catch (err) {
          console.error('Error cleaning up chart instance:', err);
        }
      }
    };
  }, [effectiveToken?.mint, scriptsLoaded, currentTimeframe, timeframeToResolution]);
  
  // Fetch chart data for token and timeframe
  const fetchChartData = async (tokenMint: string, tf: string) => {
    setIsLoading(true);
    
    try {
      // Calculate time range based on timeframe
      const now = Math.floor(Date.now() / 1000);
      let startTime = now - (7 * 24 * 60 * 60); // Default to 1 week
      
      console.log(`Fetching data for ${tokenMint}, timeframe: ${tf}, from: ${new Date(startTime * 1000).toISOString()} to: ${new Date(now * 1000).toISOString()}`);
      
      const result = await getTokenChartData(tokenMint, tf, startTime, now);
      
      if (result.status === 'success' && result.data && result.data.length > 0) {
        console.log(`Received ${result.data.length} data points for timeframe ${tf}`);
        
        // Set data in state
        setChartData(result.data);
      } else {
        console.warn('No chart data returned:', result);
        setChartData([]);
      }
    } catch (error) {
      console.error(`Error fetching chart data for timeframe ${tf}:`, error);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const customStyles = `
    /* Force chart container to fill all available space */
    .chart-container-wrapper {
      height: 100% !important;
      flex: 1 1 auto !important;
      display: flex !important;
      flex-direction: column !important;
      min-height: 0 !important;
    }
    
    /* Ensure the chart container takes up all available space */
    .chart-container {
      width: 100% !important;
      height: 100% !important;
      position: absolute !important;
      inset: 0 !important;
      overflow: hidden !important;
    }
  `;

  return (
    <div 
      className="flex flex-col bg-transparent h-full w-full" 
      style={{ 
        height: '100%', 
        overflow: 'hidden'
      }}
    >
      <style>{customStyles}</style>
      
      <div className="flex-grow flex flex-col overflow-hidden chart-container-wrapper">
        <div className="w-full h-full bg-transparent relative flex-grow flex flex-col">
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
              <div className="flex items-center gap-2">
                <RefreshCcw size={16} className="text-[#47be8f] animate-spin" />
                <span className="text-white/80 text-sm">Loading chart data...</span>
              </div>
            </div>
          ) : (
            effectiveToken ? (
              <div 
                ref={chartContainerRef} 
                className="chart-container" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0
                }}
              />
            ) : (
              <div className="text-center text-white/50 flex flex-col items-center justify-center h-full">
                <TrendingUp size={40} className="mb-4 opacity-20" />
                <p className="text-lg mb-2">No token selected</p>
                <button
                  onClick={() => setIsSearchModalOpen(true)}
                  className="mt-4 px-4 py-2 bg-[#47be8f]/20 hover:bg-[#47be8f]/30 text-[#47be8f] rounded-md text-sm transition-colors flex items-center"
                >
                  <Search size={14} className="mr-2" />
                  Search for a token
                </button>
              </div>
            )
          )}
        </div>
      </div>
      
      {/* Token Search Modal */}
      <TokenSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectToken={(token) => {
          setLocalSelectedToken(token);
          setIsSearchModalOpen(false);
        }}
        referenceElement={searchButtonRef.current}
      />
    </div>
  );
};

export default SimpleChart; 